import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ItemDetailContentData } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { ItemDetailSection } from '@/experiences/sales-portal/domain/items/components/item-detail-section';
import { OrderEntryLabel } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { PurchaseHistoryList } from '@/experiences/sales-portal/domain/items/components/purchase-history/purchase-history-list';
import { ScrollView } from 'react-native';
import { useGeneralTabSections } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-general-tab-sections';

export interface GeneralTabPanelProps {
  /** Combined panel data from {@link useItemDetailContentLoader}. */
  data: ItemDetailContentData;
  /** When true, hides GP% fields (supplier-wide GP display config). */
  isGrossProfitHidden: boolean;
  /** When true, hides the pricing info section (sales-rep visibility rules). */
  isSalesRepInfoDisabled: boolean;
  /** Row labels (availability / essential / lead time) for the Status card. */
  labels: OrderEntryLabel | undefined;
  /** Order guides the row belongs to. */
  orderGuideLabels: { level: string; orderGuideName: string }[];
}

/**
 * General tab body (read-only). Renders the sections derived by
 * {@link useGeneralTabSections} through the single {@link ItemDetailSection}
 * component.
 *
 * @param props - {@link GeneralTabPanelProps}.
 * @returns The rendered tab body.
 */
export const GeneralTabPanel: React.FC<GeneralTabPanelProps> = ({
  data,
  isGrossProfitHidden,
  isSalesRepInfoDisabled,
  labels,
  orderGuideLabels,
}) => {
  const [styles] = useStyles();
  const sections = useGeneralTabSections({
    data,
    isGrossProfitHidden,
    isSalesRepInfoDisabled,
    labels,
    orderGuideLabels,
  });

  return (
    <ScrollView
      contentContainerStyle={styles.bodyContent}
      style={styles.bodyScroll}
    >
      {sections.map(({ key, ...section }) => (
        <ItemDetailSection key={key} {...section} />
      ))}
      <PurchaseHistoryList
        entries={data.purchaseHistory}
        hideGpField={isSalesRepInfoDisabled || isGrossProfitHidden}
      />
    </ScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  bodyContent: {
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.sk.spacing.spaceSmall,
  },
  bodyScroll: {
    flex: 1,
  },
}));
